import { useMemo } from 'react'
import { FormikProvider, useFormik } from 'formik'
import { faStars } from '@fortawesome/pro-regular-svg-icons'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useAnalyticsTrack } from '@/features/amplitude'
import { getFetchError } from '@/reducers/fetch'
import { useReselector } from '@/utils/sharedHooks'
import {
  centsToCurrency,
  dollarsToCents,
  dollarsToCurrency,
} from '@/utils/currencyHelpers'

import { Form, Alert, Link, Text } from '@/ui'
import { TypographyStyles } from '@/styles'
import { getFinancialProfile } from '@/selectors/user.selectors'
import {
  UPDATE_FINANCIAL_PROFILE_KEY,
  updateFinancialProfile,
} from '@/actions/financialProfileActions'
import {
  resolveSelfW2Income,
  TAX_PROFILE_STEP,
  TaxesProfileFlowAnswerProps,
  useTaxProfileSteps,
  withholdingRecommendedMinimumPercentage,
} from './helpers'

import { TaxProfileFormFooter } from './TaxProfileFormFooter'
import {
  TaxProfileAccordions,
  useTaxProfileAccordionCopy,
} from './TaxProfileAccordions'
import { ExamplePaystub } from './ExamplePaystub'
import { selectActiveQuarterlyTaxEstimateDetails } from '@/features/admin/quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.selector'

// TODO: Migrate to V2
import { getFieldName, makeNumberSchema } from '@/componentsV1/BaseComponents'
import { useTherapistFlag, CaseStyle } from '@/utils/launchdarklyHelpers'

export const WithholdingsForm = ({
  formFlowAnswers,
  setFormFlowAnswers,
}: TaxesProfileFlowAnswerProps) => {
  const dispatch = useAppDispatch()
  const fp = useReselector(getFinancialProfile)
  const track = useAnalyticsTrack()

  const activeQuarterDetails = useReselector(
    selectActiveQuarterlyTaxEstimateDetails
  )

  const projectionFactorForYTDIncomeAndWithholdings =
    activeQuarterDetails &&
    activeQuarterDetails.bookkeepingPeriodTotalMonths > 0
      ? (Number(activeQuarterDetails.taxQuarter) * 3) /
        activeQuarterDetails.bookkeepingPeriodTotalMonths
      : 1
  const { allYtdW2Income } = resolveSelfW2Income({
    fp,
    quarterDetails: activeQuarterDetails,
  }) ?? { allYtdW2Income: centsToCurrency(0) }

  const error = useReselector(
    getFetchError,
    UPDATE_FINANCIAL_PROFILE_KEY(fp?.id)
  )
  const { goToPreviousStep, goToNextStep } = useTaxProfileSteps(
    TAX_PROFILE_STEP.withholdings
  )

  const formik = useFormik({
    enableReinitialize: true,
    validateOnMount: true,
    initialValues: {
      federalWithholding: formFlowAnswers.federalWithholding,
      stateWithholding: formFlowAnswers.stateWithholding,
    },
    onSubmit: async ({ federalWithholding, stateWithholding }) => {
      const res = await dispatch(
        updateFinancialProfile(fp?.id, {
          federalWithholdingInCents: dollarsToCents(federalWithholding),
          stateWithholdingInCents: dollarsToCents(stateWithholding),
        })
      )

      if (res) {
        setFormFlowAnswers({
          ...formFlowAnswers,
          federalWithholding,
          stateWithholding,
        })
        track('self withholdings submitted', {
          federal_withholding_in_cents: dollarsToCents(federalWithholding),
          state_withholding_in_cents: dollarsToCents(stateWithholding),
          user_id: fp?.userId ?? null,
        })
        goToNextStep(res)
      }
    },
  })

  const { values, submitForm, isValid, isSubmitting } = formik

  const federalWithholdingSchema = makeNumberSchema({
    allowedDecimals: 2,
    required: false,
  })
    .test(
      'federalWithholdingValidation',
      'Your withholdings must be less than the W-2 income you shared on a previous step. Please double-check your paystub.',
      (federalWithholding) => {
        const withholding = dollarsToCurrency(federalWithholding).value

        return !withholding || allYtdW2Income.value > withholding
      }
    )
    .test(
      'federalWithholdingGreaterThanZeroValidation',
      'Your withholdings cannot be $0. Please double-check your paystub.',
      (federalWithholding) => {
        //don't throw an error when they first arrive to the page
        if (!federalWithholding) {
          return true
        }
        const withholding = dollarsToCurrency(federalWithholding).value

        return withholding > 0
      }
    )

  const stateWithholdingSchema = makeNumberSchema({
    allowedDecimals: 2,
  }).test(
    'stateWithholdingValidation',
    'Your withholdings must be less than the W-2 income you shared on a previous step. Please double-check your paystub.',
    (stateWithholding) => {
      const withholding = dollarsToCurrency(stateWithholding).value

      return !withholding || allYtdW2Income.value > withholding
    }
  )

  const withholdingPercentage = useMemo(() => {
    const projectedWithholdings = dollarsToCurrency(
      values.federalWithholding
    ).multiply(projectionFactorForYTDIncomeAndWithholdings)

    const w2IncomeEndOfQuarter = allYtdW2Income.multiply(
      projectionFactorForYTDIncomeAndWithholdings
    )
    return Math.floor(
      projectedWithholdings.divide(w2IncomeEndOfQuarter).value * 100 // '* 100' to get formatted  percentage
    )
  }, [
    values.federalWithholding,
    projectionFactorForYTDIncomeAndWithholdings,
    allYtdW2Income,
  ])

  const faqCopy = useTaxProfileAccordionCopy()
  const replacementText = useTherapistFlag(CaseStyle.lowerCase, true)

  return (
    <FormikProvider value={formik}>
      <div className="w-full space-y-6">
        <ExamplePaystub zoomTo="withholdings" />

        <Text
          className="max-w-3xl mx-auto text-center"
          variant={TypographyStyles.DISPLAY_SM}
        >
          What are your total W-2 year-to-date tax withholdings?
        </Text>

        <Text
          className="max-w-3xl mx-auto text-center"
          variant={TypographyStyles.BODY_LG}
        >
          Please enter the total of all tax withholdings from your W-2 job(s).
          You can find these numbers on your most recent paystub(s).
        </Text>

        {error && (
          <Alert
            className="max-w-3xl mx-auto mb-4"
            variant="error"
            description={error.message}
          />
        )}
        <div className="max-w-2xl mx-auto">
          <Form.Input
            name={getFieldName<typeof values>('federalWithholding')}
            label="Year-to-Date Federal Tax Withholdings"
            required
            componentType="currency"
            schema={federalWithholdingSchema}
          />
        </div>
        {/* Under-withholding warning */}
        {Boolean(
          values.federalWithholding &&
          withholdingPercentage < withholdingRecommendedMinimumPercentage
        ) && (
          <div className="max-w-2xl mx-auto">
            <Alert
              customIconName={faStars}
              title={`You are withholding ${withholdingPercentage}% of your income`}
            >
              <Text variant={TypographyStyles.BODY_MD}>
                On average, {replacementText} who earn W-2 income withhold{' '}
                {withholdingRecommendedMinimumPercentage}% for taxes. Not
                withholding enough taxes increases your year-end tax bill. This
                is simply a recommendation for you to consider. Learn how to
                calculate your withholdings{' '}
                <Link
                  href="https://support.joinheard.com/hc/en-us/articles/22225192416151"
                  newPage
                >
                  here
                </Link>
                .
              </Text>
            </Alert>
          </div>
        )}
        <div className="max-w-2xl mx-auto mb-4">
          <Form.Input
            name={getFieldName<typeof values>('stateWithholding')}
            label="Year-to-Date State Tax Withholdings"
            required
            componentType="currency"
            schema={stateWithholdingSchema}
          />
        </div>
        <TaxProfileAccordions
          faqs={[
            faqCopy.alreadyPaidW2Withholdings,
            faqCopy.whatAreYTDTaxWithholdings,
            faqCopy.whatIfMultipleW2,
            faqCopy.whatIfHomeStateNoTax,
          ]}
        />

        <TaxProfileFormFooter
          submitOrContinue={submitForm}
          goToPreviousStep={goToPreviousStep}
          continueDisabled={!isValid || isSubmitting}
          loading={isSubmitting}
        />
      </div>
    </FormikProvider>
  )
}
